import { useState } from "react";
import { Link } from "react-router-dom";
import { useAssignSites, useRemoveFromRedList } from "../../api/sites";

/** What the office can do to the ticked rows: assign them to a provider,
 * take them off the Red List, or plan a route for them. It appears once something is ticked and acts on
 * the ticked sites only, never on the whole filter -- a filter of 300 is not a
 * selection of 300. A refusal (a blocking flag, say) is the rule's own
 * sentence, shown in the bar, and the ticks stay so nothing has to be found
 * again. */
export default function BulkBar({
  ticked,
  providers,
  onDone,
  onClear,
}: {
  ticked: ReadonlySet<number>;
  providers: readonly { id: number; name: string; active: boolean }[];
  onDone: (message: string) => void;
  onClear: () => void;
}) {
  const [providerId, setProviderId] = useState("");
  const assign = useAssignSites();
  const unlist = useRemoveFromRedList();
  const ids = [...ticked];
  const count = ids.length;
  const noun = count === 1 ? "site" : "sites";
  const busy = assign.isPending || unlist.isPending;
  const chosen = providers.find((p) => String(p.id) === providerId);

  return (
    <section className="site-bulk-bar" aria-label="Act on the ticked sites">
      <strong>
        {count} {noun} selected
      </strong>
      <span className="site-bulk-rule" aria-hidden="true" />
      <select aria-label="Assign to" value={providerId} onChange={(e) => setProviderId(e.target.value)}>
        <option value="">Assign to…</option>
        {providers
          .filter((p) => p.active)
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
      </select>
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy || chosen === undefined}
        onClick={() =>
          assign.mutate(
            { site_ids: ids, provider_id: Number(providerId) },
            {
              onSuccess: (result) => {
                unlist.reset();
                onDone(`Assigned ${result.assigned} ${result.assigned === 1 ? "site" : "sites"} to ${chosen?.name ?? "the provider"}.`);
              },
            },
          )
        }
      >
        Assign
      </button>
      <button
        type="button"
        className="btn site-bulk-ghost"
        disabled={busy}
        onClick={() =>
          unlist.mutate(ids, {
            onSuccess: (result) => {
              assign.reset();
              onDone(
                result.removed === 0
                  ? "None of those sites was on the Red List."
                  : `Took ${result.removed} ${result.removed === 1 ? "site" : "sites"} off the Red List.`,
              );
            },
          })
        }
      >
        Take off the Red List
      </button>
      {/* The ticks alone, not the register's filter: the Planner plans its
          filter narrowed to the ticks, and a search that has since hidden a
          ticked site would drop it from the route. */}
      <Link className="btn site-bulk-ghost" to={`/routing?${ids.map((id) => `site_ids=${id}`).join("&")}`}>
        Plan a route
      </Link>
      <button type="button" className="site-bulk-clear" onClick={onClear}>
        Clear selection
      </button>
      {assign.isError ? (
        <p className="site-bulk-error" role="alert">
          {assign.error.message}
        </p>
      ) : null}
      {unlist.isError ? (
        <p className="site-bulk-error" role="alert">
          {unlist.error.message}
        </p>
      ) : null}
    </section>
  );
}
